/**
 * Character metadata used by the arena UI.
 *
 * The canonical *interpretive* content (the eight-section .md files) lives in
 * `packages/characters/`. This file is the lightweight UI-facing version: names,
 * eras, and the topic prompts each figure is well-suited to debate.
 *
 * Roster is curated for broad recognition — figures most people have heard of
 * and can have an intuition about, spanning philosophy, strategy, and science.
 */

export interface CharacterMeta {
  id: string;
  name: string;
  era: string;
  tradition: string;
  blurb: string;
}

/**
 * Roster ids that have an actual portrait image at
 * `apps/web/public/assets/portraits/<id>.jpg`. Anyone NOT in this set falls
 * back to an SVG monogram with their initials so the roster cards and stage
 * portraits stay visually intact.
 */
const HAS_REAL_PORTRAIT = new Set([
  'aristotle',
  'confucius',
  'einstein',
  'machiavelli',
  'marcus-aurelius',
  'napoleon',
  'nietzsche',
  'plato',
  'socrates',
  'sun-tzu',
  // Third row — Higgsfield composite, sliced into 5 padded-square portraits.
  'alexander-hamilton',
  'ben-shapiro',
  'benjamin-netanyahu',
  'donald-trump',
  'thomas-jefferson',
]);

function initialsFor(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** Return a serif-italic monogram SVG as a data URI — used when there is no jpg yet. */
function svgPortrait(initials: string): string {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0%' stop-color='#3a2c12'/><stop offset='100%' stop-color='#140d06'/></linearGradient></defs><rect width='100' height='100' fill='url(#g)'/><text x='50' y='62' font-family='Cormorant Garamond, Times New Roman, serif' font-style='italic' font-weight='400' font-size='48' fill='#f1d27c' text-anchor='middle'>${initials}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/**
 * Resolve the portrait URL for a character. JPGs live in
 * `apps/web/public/assets/portraits/<id>.jpg`. If we don't have one, we
 * synthesize a serif-italic monogram so the UI never shows a broken image.
 */
export const portraitFor = (id: string, name?: string): string => {
  if (HAS_REAL_PORTRAIT.has(id)) return `/assets/portraits/${id}.jpg`;
  const fallbackName = name ?? findCharacter(id)?.name ?? id;
  return svgPortrait(initialsFor(fallbackName));
};

export const CHARACTERS: CharacterMeta[] = [
  {
    id: 'aristotle',
    name: 'Aristotle',
    era: '384–322 BCE',
    tradition: 'Greek philosophy',
    blurb: 'The systematist of virtue, politics, and the natural world.',
  },
  {
    id: 'confucius',
    name: 'Confucius',
    era: '551–479 BCE',
    tradition: 'Chinese philosophy',
    blurb: 'The teacher of ritual, duty, and the well-ordered life.',
  },
  {
    id: 'einstein',
    name: 'Einstein',
    era: '1879–1955',
    tradition: 'Modern physics',
    blurb: 'The mind that unwove space and time.',
  },
  {
    id: 'machiavelli',
    name: 'Machiavelli',
    era: '1469–1527',
    tradition: 'Political philosophy',
    blurb: 'The advisor who told princes what kings would not hear.',
  },
  {
    id: 'marcus-aurelius',
    name: 'Marcus Aurelius',
    era: '121–180 CE',
    tradition: 'Roman Stoicism',
    blurb: 'The emperor-philosopher who ruled the empire and his own mind.',
  },
  {
    id: 'napoleon',
    name: 'Napoleon',
    era: '1769–1821',
    tradition: 'Imperial strategy',
    blurb: 'The emperor of will, ambition, and reform.',
  },
  {
    id: 'nietzsche',
    name: 'Nietzsche',
    era: '1844–1900',
    tradition: 'German philosophy',
    blurb: 'The hammer against morality, pity, and the herd.',
  },
  {
    id: 'plato',
    name: 'Plato',
    era: '428–348 BCE',
    tradition: 'Greek philosophy',
    blurb: 'The architect of the ideal city and the world of forms.',
  },
  {
    id: 'socrates',
    name: 'Socrates',
    era: '470–399 BCE',
    tradition: 'Greek philosophy',
    blurb: 'The questioner who knew that he knew nothing.',
  },
  {
    id: 'sun-tzu',
    name: 'Sun Tzu',
    era: '544–496 BCE',
    tradition: 'Chinese military thought',
    blurb: 'The strategist who taught that the best victory needs no fight.',
  },
  // Third row — Founders and contemporary voices. Sorted alphabetically among
  // themselves so the third grid row reads tidily left to right.
  {
    id: 'alexander-hamilton',
    name: 'Hamilton',
    era: '1755–1804',
    tradition: 'American federalism',
    blurb: "The immigrant who built a republic's financial spine.",
  },
  {
    id: 'ben-shapiro',
    name: 'Shapiro',
    era: 'b. 1984',
    tradition: 'American conservatism',
    blurb: 'The rapid-fire debater who turned syllogism into a creed.',
  },
  {
    id: 'benjamin-netanyahu',
    name: 'Netanyahu',
    era: 'b. 1949',
    tradition: 'Revisionist Zionism',
    blurb: "The unyielding heir of Jabotinsky's iron wall.",
  },
  {
    id: 'donald-trump',
    name: 'Trump',
    era: 'b. 1946',
    tradition: 'American populism',
    blurb: 'The salesman who never concedes a loss.',
  },
  {
    id: 'thomas-jefferson',
    name: 'Jefferson',
    era: '1743–1826',
    tradition: 'American republicanism',
    blurb: 'The author of liberty and its deepest contradiction.',
  },
];

export function findCharacter(id: string | null): CharacterMeta | null {
  if (!id) return null;
  return CHARACTERS.find((c) => c.id === id) ?? null;
}

/**
 * Topics each character is well-suited to argue. These are debate-shaped
 * prompts — phrased as questions, designed to provoke a strong position.
 */
const CHARACTER_PROMPTS: Record<string, string[]> = {
  aristotle: [
    'Is moderation always wisdom?',
    'What is the highest human good?',
    "Should the state cultivate citizens' character?",
    'Are some lives more worth living than others?',
  ],
  confucius: [
    'Should we honor tradition over progress?',
    'What does a child owe a parent?',
    'Can a ruler be just in an unjust world?',
    'Are rituals the soul of society?',
  ],
  einstein: [
    'Is the universe knowable?',
    'Can imagination outweigh knowledge?',
    'Is God a metaphor, or a fact?',
    'Should science have moral limits?',
  ],
  machiavelli: [
    'Is it better to be feared than loved?',
    'Does the end justify the means?',
    'Should a ruler ever be honest?',
    'Is morality a luxury of the powerful?',
  ],
  'marcus-aurelius': [
    'Is duty more important than desire?',
    'Should we fear death?',
    'Are foreseeable troubles still tragedies?',
    'Is what is good for the hive good for the bee?',
  ],
  napoleon: [
    'Is great history made by great men?',
    'Is power the highest political end?',
    'Can a tyrant be a liberator?',
    'Should ambition answer to morality?',
  ],
  nietzsche: [
    'Is suffering essential to greatness?',
    'Has Christianity weakened humanity?',
    'Is pity a virtue or a poison?',
    'Do universal moral truths exist?',
  ],
  plato: [
    'Should reason rule the state?',
    'Is what we see merely a shadow of the real?',
    'Should poets be exiled from the ideal city?',
    'Can the soul be perfectly just?',
  ],
  socrates: [
    'Can virtue be taught?',
    'Is the unexamined life worth living?',
    'What is justice?',
    'Is it ever right to defy the law?',
  ],
  'sun-tzu': [
    'Is victory without battle the truest victory?',
    'Should a leader hide their strength?',
    'Is deception the heart of strategy?',
  ],
  'alexander-hamilton': [
    'Is a strong executive the friend or enemy of liberty?',
    'Is a national debt a blessing — or a curse?',
    'Should the federal government direct the economy, or stay out of it?',
    'Is honor worth dying for?',
  ],
  'ben-shapiro': [
    'Do facts care about your feelings?',
    'Can natural rights survive without God?',
    'Is the traditional family the cornerstone of a free society?',
    'Has the modern university become a cathedral of a new religion?',
  ],
  'benjamin-netanyahu': [
    'Is peace possible only from a position of strength?',
    'Should a nation ever rely on the goodwill of others?',
    'Is a nuclear Iran an acceptable risk?',
    'Are international institutions friends, or threats, to nation-states?',
  ],
  'donald-trump': [
    'Is winning the only thing that matters?',
    'Is honesty a liability in politics?',
    'Are tariffs strength, or self-harm?',
    'Does the dealmaker make a better statesman than the ideologue?',
  ],
  'thomas-jefferson': [
    'Should each generation rewrite its own constitution?',
    'Is the small farmer the natural citizen of a republic?',
    'Can a slave-owner write a creed of human equality?',
    'Should the states resist the federal government?',
  ],
};

/**
 * Pair-specific prompts. Exploit the *tension* between two specific figures —
 * topics where the disagreement is sharpest and most watchable.
 *
 * Keys are the two ids sorted alphabetically and joined with ':'.
 */
const PAIR_PROMPTS: Record<string, string[]> = {
  'aristotle:einstein': [
    'Is the natural world knowable through reason alone?',
    'Has science overtaken philosophy?',
  ],
  'aristotle:nietzsche': [
    'Is moderation a virtue — or a failure of nerve?',
    'Is happiness a worthy goal, or a sedative?',
  ],
  'aristotle:plato': [
    'Are virtues knowable in this world, or only beyond it?',
    'Is the philosopher fit to rule, or fit only to question?',
  ],
  'confucius:nietzsche': [
    'Should we revere the past, or destroy it?',
  ],
  'confucius:sun-tzu': [
    'Is order kept by virtue, or by strategy?',
  ],
  'einstein:nietzsche': [
    'Is truth discovered, or willed?',
    'Does the universe need meaning?',
  ],
  'machiavelli:marcus-aurelius': [
    'Should a ruler be virtuous, or effective?',
    'Is honesty the floor of leadership, or its ceiling?',
  ],
  'machiavelli:socrates': [
    'Is honesty a luxury, or the only ground of life?',
    'Is power its own justification?',
  ],
  'marcus-aurelius:nietzsche': [
    'Should we accept fate, or break it?',
    'Is suffering a teacher, or a wound?',
  ],
  'napoleon:socrates': [
    'Is the philosopher or the conqueror the truer mover of history?',
  ],
  'napoleon:sun-tzu': [
    'Is the greatest general the one who avoids battle?',
    'Is brilliance worthless without restraint?',
  ],
  'machiavelli:napoleon': [
    'What is power for?',
    'Should the prince obey, or be obeyed?',
  ],
  'nietzsche:plato': [
    'Should we destroy the world of forms?',
    'Is the ideal a guide, or a chain?',
  ],
  'nietzsche:socrates': [
    'Is the examined life noble — or the death of instinct?',
    'Is questioning everything wisdom, or weakness?',
  ],
  'plato:socrates': [
    'Did the student betray the teacher?',
    'Is writing philosophy a corruption of it?',
  ],
  'einstein:plato': [
    'Is mathematics a discovery, or an invention?',
  ],
  'aristotle:confucius': [
    'East and West: are the virtues the same?',
  ],

  // Third-row pairings — sharpest tensions across the new additions and the
  // existing roster. Keys remain alphabetical-by-id, joined with ':'.
  'alexander-hamilton:thomas-jefferson': [
    'Commercial republic, or agrarian one?',
    'Strong central government, or jealous states?',
    'Should the federal courts be the guardians of the Constitution?',
  ],
  'donald-trump:thomas-jefferson': [
    'Is the will of the people the surest guide to government?',
    'Should a president ever be above the institutions he leads?',
  ],
  'alexander-hamilton:donald-trump': [
    'What is the proper temperament of executive power?',
    'Is the rally, or the report, the seat of legitimacy?',
  ],
  'donald-trump:machiavelli': [
    'Is it better to be feared than loved?',
    'Should a prince ever apologize?',
  ],
  'ben-shapiro:nietzsche': [
    'Can morality survive the death of God?',
    'Is reason the friend or the enemy of life?',
  ],
  'ben-shapiro:socrates': [
    'Is the syllogism the bedrock of truth — or its shadow?',
    'Can a debate ever produce wisdom?',
  ],
  'ben-shapiro:benjamin-netanyahu': [
    'What does Israel owe the world, and what does the world owe Israel?',
  ],
  'benjamin-netanyahu:sun-tzu': [
    'Is deterrence the highest form of victory?',
    'Should the strong reveal their strength, or conceal it?',
  ],
  'confucius:thomas-jefferson': [
    'Is the trained scholar or the small farmer the citizen a republic needs?',
  ],
  'napoleon:thomas-jefferson': [
    'Is the republic of laws weaker than the empire of will?',
  ],
};

/** Starter prompts when no characters are selected yet. */
const STARTER_PROMPTS = [
  'What is the highest good?',
  'Is morality universal, or invented?',
  'Should power serve justice — or define it?',
  'Can virtue be taught?',
  'Is suffering necessary for meaning?',
];

/**
 * Suggested debate topics for the current selection.
 *
 * Priority:
 *   1. Pair-specific prompts (when both selected and a pair entry exists)
 *   2. Each selected character's individual prompts
 *   3. Starter prompts (when nothing selected)
 *
 * Deduplicated and capped at `max`.
 */
export function getSuggestions(
  leftId: string | null,
  rightId: string | null,
  max = 5,
): string[] {
  if (!leftId && !rightId) return STARTER_PROMPTS.slice(0, max);

  const out: string[] = [];

  if (leftId && rightId) {
    const key = [leftId, rightId].sort().join(':');
    if (PAIR_PROMPTS[key]) out.push(...PAIR_PROMPTS[key]);
  }

  if (leftId) out.push(...(CHARACTER_PROMPTS[leftId] ?? []));
  if (rightId) out.push(...(CHARACTER_PROMPTS[rightId] ?? []));

  return [...new Set(out)].slice(0, max);
}
